import { SearchIcon } from '@euler/app/tv/components/dashboard/icons/SearchIcon';
import { usePresentationTheme } from '@euler/app/tv/theme';
import { FontFamily } from '@euler/components/typography';
import { useResponsiveContext } from '@euler/functions/responsive/ResponsiveContext';
import { useBehaviorSubject, usePersistFn } from '@euler/utils/hooks';
import { memo, useState } from 'react';
import {
  Keyboard,
  NativeSyntheticEvent,
  Platform,
  StyleProp,
  TextInput,
  TextInputKeyPressEventData,
  TouchableOpacity,
  View,
  ViewStyle,
} from 'react-native';
import { BehaviorSubject } from 'rxjs';

export const SearchInput = memo(
  ({
    query,
    style,
    onQueryChange,
    onSearch,
  }: {
    query: string;
    style?: StyleProp<ViewStyle>;
    onQueryChange: (query: string) => void;
    onSearch?: () => void;
  }) => {
    const theme = usePresentationTheme();
    const { rpx } = useResponsiveContext();

    const onEndEditing = usePersistFn(() => {
      onSearch?.();
    });

    const onKeyPress = usePersistFn(
      (e: NativeSyntheticEvent<TextInputKeyPressEventData>) => {
        if (Platform.OS !== 'web') return;
        if (e.nativeEvent.key === 'Enter') {
          onSearch?.();
        }
      },
    );

    return (
      <View
        css={`
          width: ${rpx(320)}px;
          margin-left: ${rpx(64)}px;
          height: ${rpx(62)}px;
          border-radius: ${rpx(62) / 2}px;
          border-width: 1px;
          border-color: ${theme.dashboard.navbar.searchInput.borderColor};
          padding-left: ${rpx(28)}px;
          padding-right: ${rpx(46)}px;
        `}
        style={style}
      >
        <TextInput
          value={query}
          placeholder="车牌搜索"
          keyboardType="ascii-capable"
          clearButtonMode="while-editing"
          placeholderTextColor={
            theme.dashboard.navbar.searchInput.placeholderTextColor
          }
          onChangeText={onQueryChange}
          onEndEditing={onEndEditing}
          returnKeyType={'search'}
          onKeyPress={Platform.OS === 'web' ? onKeyPress : undefined}
          css={`
            flex: 1;
            font-family: ${FontFamily.NotoSans.Regular};
            font-size: ${rpx(24)}px;
            line-height: ${rpx(32)}px;
            color: ${theme.dashboard.navbar.searchInput.textColor};
            text-transform: uppercase;
            padding: 0;
          `}
        />
        <TouchableOpacity
          onPress={onSearch}
          css={`
            position: absolute;
            right: 0;
            top: 0;
            bottom: 0;
            width: ${rpx(60)}px;
            align-items: center;
            justify-content: center;
          `}
        >
          <SearchIcon
            width={rpx(31)}
            height={rpx(32)}
            color={theme.dashboard.navbar.searchInput.icon.color}
          />
        </TouchableOpacity>
      </View>
    );
  },
);

export const WiredSearchInput = memo(
  ({
    query$,
    style,
    instanceSearchEnabled,
  }: {
    /** when enabled, search will be executed on the fly w/o explicit action */
    instanceSearchEnabled?: boolean;
    query$: BehaviorSubject<string>;
    style?: StyleProp<ViewStyle>;
  }) => {
    const [query, updateQuery] = useBehaviorSubject(query$);
    const [localQuery, updateLocalQuery] = useState<string>(query);

    const onSearch = usePersistFn(() => {
      updateQuery(localQuery);
      Keyboard.dismiss();
    });

    const onLocalQueryChange = usePersistFn((s: string) => {
      updateLocalQuery(s);
      if (!s) updateQuery(s);
    });
    return (
      <SearchInput
        query={instanceSearchEnabled ? query : localQuery}
        onQueryChange={instanceSearchEnabled ? updateQuery : onLocalQueryChange}
        onSearch={instanceSearchEnabled ? undefined : onSearch}
        style={style}
      />
    );
  },
);
